package com.algorix.web

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.get
import kotlin.js.json
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.pow

/** One app scene: the frosted window and the volume of devices that tilt and assemble on scroll. */
class Vol {
    var scene: HTMLElement? = null
    var el: HTMLElement? = null
    var inner: HTMLElement? = null
    var win: HTMLElement? = null
    var px = 0.0
    var py = 0.0
    var rx = 0.0
    var ry = 0.0
    var wy: Double? = null
    var asm: Double? = null
    var vis = false
}

/**
 * Scroll parallax, pointer tilt and the assembly of the volumes, the KMP board and the about plates.
 * The current values live in plain fields so a recomposition re-applies them.
 */
class Depth {
    private val reduce = reduceMotion
    private val fine = media("(hover: hover) and (pointer: fine)")

    val vols = List(3) { Vol() }
    var hero: HTMLElement? = null
    var kmp: HTMLElement? = null
    var plates: HTMLElement? = null

    var hp: Double? = null
        private set
    var kmpAsm: Double? = null
        private set
    var platesAsm: Double? = null
        private set
    var kmp3d by mutableStateOf(false)
        private set

    private var kmpVis = false
    private var platesVis = false
    private var firstPass = true

    fun start() {
        if (reduce) return
        vols.forEach { v ->
            val scene = v.scene ?: return@forEach
            if (!fine) return@forEach
            scene.addEventListener("pointermove", { e ->
                e as MouseEvent
                val r = v.el?.getBoundingClientRect() ?: return@addEventListener
                v.px = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1.0, 1.0)
                v.py = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1.0, 1.0)
                FrameLoop.kick()
            })
            scene.addEventListener("pointerleave", {
                v.px = 0.0; v.py = 0.0; FrameLoop.kick()
            })
        }
        if (hasIntersectionObserver) {
            val io = IntersectionObserver({ en, _ ->
                en.forEach { x ->
                    vols.forEach { v -> if (v.el == x.target) v.vis = x.isIntersecting }
                    if (x.target == kmp) kmpVis = x.isIntersecting
                    if (x.target == plates) platesVis = x.isIntersecting
                }
                FrameLoop.kick()
            }, json("rootMargin" to "120px 0px"))
            vols.forEach { v -> v.el?.let(io::observe) }
            kmp?.let(io::observe)
            plates?.let(io::observe)
        } else {
            vols.forEach { it.vis = true }
            kmpVis = true
            platesVis = true
        }
        window.addEventListener("scroll", { FrameLoop.kick() }, json("passive" to true))
        window.addEventListener("resize", { FrameLoop.kick() })
        FrameLoop.add { update() }
        FrameLoop.kick()
    }

    /**
     * Assembly progress: 0 while an element is below the fold, 1 once its centre reaches the middle of
     * the viewport (easeOutCubic). Everything is settled at the page end.
     */
    private fun assembly(top: Double, height: Double, vh: Double, atEnd: Boolean): Double {
        if (atEnd) return 1.0
        val q = clamp((vh - top) / (vh * 0.5 + height * 0.5), 0.0, 1.0)
        return 1 - (1 - q).pow(3)
    }

    private fun update(): Boolean {
        var more = false
        val vh = window.innerHeight.toDouble().takeIf { it > 0 } ?: 800.0
        val wide = window.innerWidth > 900
        val all = firstPass
        firstPass = false
        val scrollY = window.scrollY
        val atEnd = scrollY + vh >= (document.documentElement?.scrollHeight ?: 0) - 2
        hero?.let { h ->
            val v = clamp(scrollY / max(1, h.offsetHeight), 0.0, 1.0)
            if (abs(v - (hp ?: -1.0)) > 0.0005) {
                hp = v; h.cssVar("--hp", v.fx(4))
            }
        }
        for (v in vols) {
            val el = v.el ?: continue
            val inner = v.inner ?: continue
            if (!(v.vis || all || atEnd)) continue
            val r = el.getBoundingClientRect()
            val a = assembly(r.top, r.height, vh, atEnd)
            if (abs(a - (v.asm ?: -1.0)) > 0.001) {
                v.asm = a; v.scene?.cssVar("--asm", a.fx(4))
            }
            if (!v.vis) continue
            val sp = clamp((r.top + r.height / 2 - vh / 2) / vh, -1.0, 1.0)
            val tx = clamp(sp * 6 - v.py * 4, -6.0, 6.0)
            val ty = clamp(v.px * 6, -6.0, 6.0)
            v.rx += (tx - v.rx) * 0.12
            v.ry += (ty - v.ry) * 0.12
            if (abs(tx - v.rx) > 0.02 || abs(ty - v.ry) > 0.02) {
                more = true
            } else {
                v.rx = tx; v.ry = ty
            }
            inner.cssVar("--rx", v.rx.fx(3) + "deg")
            inner.cssVar("--ry", v.ry.fx(3) + "deg")
            v.wy = if (wide) sp * -28 else null
            v.win?.cssVar("--wy", v.wy?.let { it.fx(1) + "px" })
        }
        kmp?.let { k ->
            if (kmpVis || all || atEnd) {
                val kr = k.getBoundingClientRect()
                val a = if (atEnd) 1.0 else 1 - (1 - clamp((vh - kr.top) / (vh * 0.75), 0.0, 1.0)).pow(3)
                if (abs(a - (kmpAsm ?: -1.0)) > 0.001) {
                    kmpAsm = a
                    k.cssVar("--asm", a.fx(4))
                    kmp3d = a < 0.999
                }
            }
        }
        plates?.let { p ->
            if (platesVis || all || atEnd) {
                val r = p.getBoundingClientRect()
                val a = assembly(r.top, r.height, vh, atEnd)
                if (abs(a - (platesAsm ?: -1.0)) > 0.001) {
                    platesAsm = a; p.cssVar("--asm", a.fx(4))
                }
            }
        }
        return more
    }
}

/** Ambient room tint (two layers cross-fading) and the current-section marker in the nav. */
class Room {
    val tintZone = mutableStateListOf("", "")
    var tintOn by mutableStateOf(-1)
        private set
    var current by mutableStateOf("")
        private set
    private var zone = ""
    private var last = 1

    private fun setTint(z: String) {
        if (z == zone) return
        zone = z
        if (z.isEmpty()) {
            tintOn = -1; return
        }
        last = 1 - last
        tintZone[last] = z
        tintOn = last
    }

    fun start() {
        if (!hasIntersectionObserver) return
        val io = IntersectionObserver({ en, _ ->
            en.forEach { x ->
                if (!x.isIntersecting) return@forEach
                setTint(x.target.getAttribute("data-zone") ?: "")
                current = x.target.getAttribute("data-nav") ?: ""
            }
        }, json("rootMargin" to "-48% 0px -48% 0px"))
        document.querySelectorAll("[data-zone]").asList().forEach { io.observe(it as Element) }
    }
}
